import SendIcon from '@mui/icons-material/Send';
import { Container } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button, TextField } from '@verantos-dev/shared-components';

export type ChatWindowUserInputFieldProps = {
  /**
   * The text to display
   */
  text: string;

  /**
   * Whether the RA is ready to accept messages
   * This is used to determine whether the user can send a message
   * and whether the input field is disabled
   */
  isRaReady: boolean;

  /**
   * Whether the user is waiting for a response
   */
  isWaitingForResponse: boolean;

  /**
   * Whether the user is allowed to submit a message
   */
  allowMessageSubmit: boolean;

  /**
   * Whether the user is the owner of the chat
   */
  isOwner: boolean;

  /**
   * The function to call when the text changes
   */
  onTextChange: (text: string) => void;

  /**
   * The function to call when the user sends a message
   */
  onSendMessage: () => void;
};

export const ChatWindowUserInputField = ({
  text,
  isOwner,
  isWaitingForResponse,
  isRaReady,
  allowMessageSubmit,
  onTextChange,
  onSendMessage,
}: ChatWindowUserInputFieldProps) => {
  const intl = useIntl();

  const onChange = (_: string, value: string) => {
    onTextChange(value);
  };

  let placeholder = intl.formatMessage({
    id: 'projects.researchAssistant.chatWindow.inputField.placeholder.isOwner',
  });
  if (!isOwner) {
    placeholder = intl.formatMessage({
      id: 'projects.researchAssistant.chatWindow.inputField.placeholder.isNotOwner',
    });
  } else if (!allowMessageSubmit) {
    placeholder = intl.formatMessage({
      id: 'projects.researchAssistant.chatWindow.inputField.placeholder.invalidStatus',
    });
  } else if (!isRaReady) {
    placeholder = intl.formatMessage({
      id: 'projects.researchAssistant.chatWindow.inputField.placeholder.raNotReady',
    });
  }

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-bottom',
        padding: '10px 0 !important',
        margin: '0',
        borderRadius: '10px',
        width: '100%',
      }}
      maxWidth={false}
    >
      <TextField
        id="chatInputField"
        inputRef={(input) => input && input.focus()}
        width={'auto'}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
        displayLabel=""
        multiline
        autoFocus={true}
        value={text}
        placeholder={placeholder}
        readOnly={!isOwner || !allowMessageSubmit || !isRaReady}
        onChange={onChange}
        onKeyDown={(event) => {
          // only allow newline if shift or ctrl is pressed with enter
          if (
            isWaitingForResponse &&
            !isRaReady &&
            event.key === 'Enter' &&
            !(event.shiftKey || event.ctrlKey)
          ) {
            event.preventDefault(); // Prevents a new line from being added
          }

          // send message if enter is pressed without shift or ctrl
          if (
            event.key === 'Enter' &&
            !(event.shiftKey || event.ctrlKey) &&
            !isWaitingForResponse &&
            isRaReady
          ) {
            event.preventDefault(); // Prevents a new line from being added
            onSendMessage();
          }
        }}
      />
      <Button
        id="chatInputButton"
        variant="contained"
        color="primary"
        size="large"
        sx={{
          height: '100%',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          boxShadow: 'none',
        }}
        displayLabel={<SendIcon />}
        onClickHandler={onSendMessage}
        disabled={isWaitingForResponse || !allowMessageSubmit || !isOwner || !isRaReady}
      />
    </Container>
  );
};
